import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { FaUser, FaLock, FaSignInAlt } from 'react-icons/fa';
import { requestNotificationPermission } from '../utils/desktopNotificationService';
import {
    isUserHR,
    isHrLoginSoundEnabled,
    playHrLoginSound,
    fetchHrLoginSoundSetting
} from '../utils/hrAudioService';

const Login = () => {
    const navigate = useNavigate();
    const { login, isAuthenticated } = useAuth();

    // Redirect to dashboard if already logged in
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/dashboard', { replace: true });
        }
        // Pre-fetch latest HR sound configuration from Firestore
        fetchHrLoginSoundSetting().catch(() => {});
    }, [isAuthenticated, navigate]);

    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await login(formData.email, formData.password);
            const loggedInUser = response?.data?.user;

            // Check if logged-in user is HR and play sugarcane juice sound for 4 seconds if enabled
            if (isUserHR(loggedInUser) && isHrLoginSoundEnabled()) {
                playHrLoginSound(4);
            }

            // Request native Desktop Notification permission on user login
            requestNotificationPermission().catch(() => {});
            toast.success('Login successful!');
            navigate('/dashboard');
        } catch (err) {
            setError(err.message || 'Login failed. Please check your credentials.');
            toast.error(err.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page" style={{
            minHeight: '100vh',
            background: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'Inter', sans-serif"
        }}>
            <Container>
                <Row className="justify-content-center">
                    <Col md={8} lg={6} xl={5}>
                        <div className="text-center mb-4 animate__animated animate__fadeIn">
                            <img
                                src="/DIGILOGO.jpg"
                                alt="Digi Sahyadri Logo"
                                style={{
                                    width: '240px',
                                    height: 'auto',
                                    borderRadius: '12px',
                                    marginBottom: '10px'
                                }}
                            />
                        </div>

                        <Card className="shadow-sm" style={{
                            background: '#f8fafc',
                            borderRadius: '20px',
                            border: '2px solid #000000'
                        }}>
                            <Card.Body className="p-4 p-md-5">
                                <div className="mb-4 text-center">
                                    <h3 className="fw-bold text-dark mb-2">Welcome Back</h3>
                                    <p className="text-muted small">Please sign in to continue</p>
                                </div>

                                {error && (
                                    <Alert variant="danger" className="border-0 rounded-3 small py-2 mb-4">
                                        {error}
                                    </Alert>
                                )}

                                <Form onSubmit={handleSubmit}>
                                    <Form.Group className="mb-4" controlId="email">
                                        <Form.Label className="text-uppercase small fw-bold text-secondary mb-2">Email Address</Form.Label>
                                        <div className="position-relative">
                                            <FaUser className="position-absolute translate-middle-y top-50 start-0 ms-3 text-muted" style={{ zIndex: 5 }} />
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                placeholder="Enter your email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                className="ps-5 py-3 border-1 bg-white rounded-3 shadow-none"
                                                style={{ fontSize: '0.95rem', borderColor: '#e2e8f0' }}
                                            />
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-4" controlId="password">
                                        <Form.Label className="text-uppercase small fw-bold text-secondary mb-2">Password</Form.Label>
                                        <div className="position-relative">
                                            <FaLock className="position-absolute translate-middle-y top-50 start-0 ms-3 text-muted" style={{ zIndex: 5 }} />
                                            <Form.Control
                                                type="password"
                                                name="password"
                                                placeholder="Enter your password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                required
                                                className="ps-5 py-3 border-1 bg-white rounded-3 shadow-none"
                                                style={{ fontSize: '0.95rem', borderColor: '#e2e8f0' }}
                                            />
                                        </div>
                                    </Form.Group>

                                    <div className="d-flex justify-content-between align-items-center mb-4 px-1">
                                        <Form.Check
                                            type="checkbox"
                                            id="remember-me"
                                            label={<span className="small text-muted">Remember me</span>}
                                        />
                                        <Link to="/forgot-password" size="sm" className="text-decoration-none small fw-bold text-primary">
                                            Forgot Password?
                                        </Link>
                                    </div>

                                    <Button
                                        variant="primary"
                                        type="submit"
                                        className="w-100 py-3 rounded-3 fw-bold shadow-sm border-0"
                                        style={{
                                            background: '#0056b3',
                                            fontSize: '1rem'
                                        }}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <div className="d-flex align-items-center justify-content-center">
                                                <span className="spinner-border spinner-border-sm me-3" role="status" aria-hidden="true" />
                                                Logging in...
                                            </div>
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center">
                                                <FaSignInAlt className="me-2" />
                                                Login
                                            </div>
                                        )}
                                    </Button>
                                </Form>
                            </Card.Body>
                        </Card>

                        <div className="text-center mt-4 text-muted small">
                            &copy; {new Date().getFullYear()} Digi Sahyadri
                        </div>
                    </Col>
                </Row>
            </Container>

            <style>
                {`
                    .form-control:focus {
                        border-color: #0056b3 !important;
                        box-shadow: 0 0 0 0.2rem rgba(0, 86, 179, 0.1) !important;
                    }
                `}
            </style>
        </div>
    );
};

export default Login;
